/**
 * GameErrorBoundary - Error boundary specifically for game boards.
 *
 * Catches errors in game components and provides game-specific recovery options.
 */

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import {
  reportErrorToBackend,
  createReactErrorReport,
} from "../lib/errorReporting";
import { logger } from "../lib/logger";

interface Props {
  children: ReactNode;
  gameId?: string;
  sessionId?: string;
  onReturnToLobby?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class GameErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { gameId, sessionId } = this.props;

    // Log to console in development
    console.error("GameErrorBoundary caught an error:", error, errorInfo);

    // Log to structured logger
    logger.error("game_board_error", {
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack,
      },
      componentStack: errorInfo.componentStack,
      gameId,
      sessionId,
      timestamp: new Date().toISOString(),
    });

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Report error to backend telemetry endpoint
    const report = createReactErrorReport(
      error,
      errorInfo.componentStack || undefined,
    );
    reportErrorToBackend({
      ...report,
      context: {
        ...report.context,
        gameId,
        sessionId,
      },
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReturnToLobby = () => {
    const { onReturnToLobby } = this.props;
    if (onReturnToLobby) {
      onReturnToLobby();
    }
    this.handleReset();
  };

  handleReturnHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      const { gameId } = this.props;

      return (
        <div
          className="min-h-screen flex items-center justify-center p-4"
          style={{ backgroundColor: "var(--color-background)" }}
        >
          <div
            className="max-w-2xl w-full rounded-lg p-8"
            style={{
              backgroundColor: "var(--color-surface)",
              border: "6px solid var(--color-error, #ef4444)",
              boxShadow: "12px 12px 0px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="text-4xl">🎮</div>
              <div>
                <h1
                  className="text-2xl font-bold mb-2"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  Game Error
                </h1>
                <p style={{ color: "var(--color-text-secondary)" }}>
                  Something went wrong while playing
                  {gameId ? ` ${gameId.replace(/-/g, " ")}` : " the game"}. Your
                  progress may not be saved.
                </p>
              </div>
            </div>

            {/* Error Details (collapsed by default) */}
            {this.state.error && (
              <details
                className="p-4 rounded mb-6"
                style={{ backgroundColor: "var(--color-background)" }}
              >
                <summary
                  className="cursor-pointer hover:opacity-80 font-medium"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  Technical Details (for developers)
                </summary>
                <div className="mt-4 space-y-2">
                  <div>
                    <span
                      className="font-semibold"
                      style={{ color: "var(--color-error, #ef4444)" }}
                    >
                      Error:
                    </span>
                    <pre
                      className="text-sm mt-1 overflow-x-auto"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      {this.state.error.toString()}
                    </pre>
                  </div>
                  {this.state.error.stack && (
                    <div>
                      <span
                        className="font-semibold"
                        style={{ color: "var(--color-error, #ef4444)" }}
                      >
                        Stack Trace:
                      </span>
                      <pre
                        className="text-xs mt-1 overflow-x-auto max-h-64"
                        style={{ color: "var(--color-text-muted)" }}
                      >
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Actions */}
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={this.handleReset}
                className="flex-1 min-w-[140px] px-6 py-3 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: "var(--color-primary)",
                  color: "var(--color-text-on-primary, white)",
                }}
              >
                Try Again
              </button>
              <button
                onClick={this.handleReturnHome}
                className="flex-1 min-w-[140px] px-6 py-3 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: "var(--color-surface-secondary)",
                  color: "var(--color-text-primary)",
                  border: "2px solid var(--color-border)",
                }}
              >
                Return Home
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: "var(--color-surface-secondary)",
                  color: "var(--color-text-primary)",
                  border: "2px solid var(--color-border)",
                }}
              >
                Reload Page
              </button>
            </div>

            {/* Help Text */}
            <div
              className="mt-6 text-sm"
              style={{ color: "var(--color-text-muted)" }}
            >
              <p>If this problem persists:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Try refreshing the page</li>
                <li>Clear your browser cache</li>
                <li>Check the browser console for more details (F12)</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
